import { useNavigate, useParams } from "react-router-dom";
import styles from "./EmployeesAdministrationEmployee.module.scss";
import { DefaultSection } from "../../../../common/components/common/DefaultSection";
import { IEmployeesAdministrationEmployee } from "./models/employeesAdministrationEmployeeModel";
import { useMemo, useState } from "react";
import { IconType } from "../../../../common/icons/Icon";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import {
  constructEmployeeEditHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SpecificPageNotFound } from "../../../../common/components/errorPages/SpecificPageNotFound";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { SectionInfoRow } from "../../../../common/components/common/SectionInfoRow";
import { AppLink } from "../../../../common/components/button/link/Link";
import { useAuthenticationFunctions } from "../../../authentication/utils/AuthenticationUtility";
import { EmployeeServicesDialog } from "./components/EmployeeServicesDialog";
import { useEmployeeServices } from "./hooks/useEmployeeServices";
import { useAppContext } from "../../../../AppContext";
import { UserRoleLabels } from "../../../../common/labels";
import { UserRole } from "../../../../common/enums";
import { HistoryDialog } from "../../../../common/components/history/HistoryDialog";
import { EMPLOYEE_HISTORY_CONFIG } from "../../../../common/utilities/userAuditLogUtility";
import { outranks } from "../../../../common/utilities/roleUtility";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";

export const EmployeesAdministrationEmployee = () => {
  const { showServicesTypeOptions } = useAppContext();
  const { id } = useParams();
  const [showServicesDialog, setShowServicesDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const navigate = useNavigate();

  const {
    userHasPermissionsToGeneratePasswordResetLink,
    onTryGenerateAndCopyPasswordResetLink,
  } = useAuthenticationFunctions();
  const { currentUserRole } = useAuthentication();

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructEmployeeEditHref(Number(id)),
    rawPath: routePaths.employeeEdit,
  });

  const backToEmployees = () => {
    navigate(routePaths.employees);
  };

  const { data: result, isLoading } =
    useApiQuery<IEmployeesAdministrationEmployee>({
      queryKey: [endpointsMetadata.employee.getEmployee, id],
      endpoint: `${endpointsMetadata.employee.getEmployee}?employeeId=${id}`,
      enabled: id !== undefined,
      globalIsLoading: false,
    });

  const { services } = useEmployeeServices(Number(id ?? 0));

  const currentEmployee = useMemo(() => result, [result]);

  // The link signs whoever opens it in as the employee, so the backend gives it
  // only for someone below the caller's role (not the owner for a manager).
  const canGeneratePasswordResetLink =
    !!userHasPermissionsToGeneratePasswordResetLink &&
    outranks(currentUserRole, currentEmployee?.role);

  if (!currentEmployee && !isLoading) {
    return (
      <SpecificPageNotFound
        text={"За съжаление няма такъв служител."}
        buttonText={"Обратно към списъка със служители"}
        onButtonClick={backToEmployees}
      />
    );
  }
  return (
    <div className={styles.root}>
      <DefaultSection className={styles.root_title}>
        <div className={styles.root_header_title}>
          <SkeletonItem
            width={300}
            isLoading={isLoading}
            value={`${currentEmployee?.firstName} ${currentEmployee?.lastName}`}
          />
        </div>
        <div className={styles.root_header_email}>
          <SkeletonItem
            width={150}
            isLoading={isLoading}
            value={
              <>
                {currentEmployee?.email} •{" "}
                {UserRoleLabels[currentEmployee?.role ?? UserRole.Employee_Instructor]}
              </>
            }
          />
        </div>
      </DefaultSection>

      <DefaultSection className={styles.root_info_section}>
        <SectionInfoRow
          label={"ID на служителя"}
          data={currentEmployee?.id}
          isLoading={isLoading}
          skekletonWidth={70}
        />

        <SectionInfoRow
          label={"Телефонен номер"}
          data={
            currentEmployee?.phoneNumber ? (
              <a
                className={styles.root_info_section_phone}
                href={`tel:${currentEmployee.phoneNumber.replace(/\s+/g, "")}`}
              >
                {currentEmployee.phoneNumber}
              </a>
            ) : (
              "N/A"
            )
          }
          isLoading={isLoading}
          skekletonWidth={150}
        />

        <SectionInfoRow
          label={"Добавен на"}
          data={dateUtility.formatDate(currentEmployee?.dateAdded)}
          isLoading={isLoading}
          skekletonWidth={120}
        />

        <SectionInfoRow
          label={"Прекратен ли е служителя"}
          data={currentEmployee?.hasResigned ? "✓" : "X"}
          isLoading={isLoading}
          skekletonWidth={50}
        />

        <SectionInfoRow
          label={"Вижда само своите класове"}
          data={currentEmployee?.viewOnlyOwnEvents ? "✓" : "X"}
          isLoading={isLoading}
          skekletonWidth={50}
        />

        <RenderWhen condition={!currentEmployee?.viewOnlyOwnEvents}>
          <SectionInfoRow
            label={"Не отваря класовете на другите"}
            data={currentEmployee?.lockOthersEvents ? "✓" : "X"}
            isLoading={isLoading}
            skekletonWidth={50}
          />
        </RenderWhen>

        <RenderWhen condition={!!currentEmployee?.dateResigned}>
          <SectionInfoRow
            label={"Дата на прекратяване"}
            data={dateUtility.formatDate(currentEmployee?.dateResigned)}
            isLoading={isLoading}
            skekletonWidth={130}
          />
        </RenderWhen>

        <RenderWhen condition={canGeneratePasswordResetLink}>
          <SectionInfoRow
            label={"Линк за задаване на парола"}
            skekletonWidth={150}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() =>
                  onTryGenerateAndCopyPasswordResetLink(Number(id ?? 0))
                }
                label={"генерирай >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={showServicesTypeOptions}>
          <SectionInfoRow
            label={"Извършвани услуги"}
            skekletonWidth={100}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => setShowServicesDialog(true)}
                label={"виж >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>

        <RenderWhen condition={!!currentEmployee}>
          <SectionInfoRow
            label={"История"}
            skekletonWidth={100}
            data={
              <AppLink
                className={styles.root_info_section_link}
                onClick={() => setShowHistoryDialog(true)}
                label={"виж >"}
              />
            }
            isLoading={isLoading}
          />
        </RenderWhen>
      </DefaultSection>

      <EmployeeServicesDialog
        visible={showServicesDialog}
        onHide={() => setShowServicesDialog(false)}
        services={services}
        isLoading={isLoading}
        employeeName={`${result?.firstName} ${result?.lastName}`}
      />

      {currentEmployee && (
        <HistoryDialog
          entityId={currentEmployee.id}
          entityCreatedOn={String(currentEmployee.dateAdded)}
          showDialog={showHistoryDialog}
          onHide={() => setShowHistoryDialog(false)}
          label="История на служителя"
          config={EMPLOYEE_HISTORY_CONFIG}
        />
      )}
    </div>
  );
};
